import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Paper, Typography,
         Chip, IconButton, TextField, MenuItem, Alert, CircularProgress, Box, Divider,
         useMediaQuery, Tooltip, Tabs, Tab, Switch, FormControlLabel } from '@mui/material';
import { Close, PersonAdd, Edit, LockReset, Block, CheckCircle, AdminPanelSettings, Person,
         Business, AddBusiness, PlaylistAdd, Campaign, EventNote } from '@mui/icons-material';
import { auth } from '../Common';

const VUOTO = { nome: '', cognome: '', username: '', email: '', ruolo: 'operatore', password: '', aziendaId: '' };
const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Email dell'utente: facoltativa, ma senza "Non ricordi la password?" non gli arriva. */
function CampoEmail({ valore, onChange, errore }) {
  return (
    <TextField size="small" type="email" label="Email (facoltativa)" value={valore.email || ''} fullWidth
               error={!!errore} autoComplete="off" inputProps={{ autoCapitalize: 'none', spellCheck: false }}
               helperText={errore || "Serve per «Non ricordi la password?»: senza, la password si reimposta solo da qui"}
               onChange={(e) => onChange({ ...valore, email: e.target.value.trim() })} />
  );
}

/** Il nome come lo compone il server: nome e iniziale del cognome ("Mario R."). Solo anteprima. */
const nomeBreve = (nome, cognome) => {
  const n = nome.trim().replace(/\s+/g, ' ');
  const c = cognome.trim();
  return c ? `${n} ${c[0].toLocaleUpperCase('it')}.` : n;
};
/** Il contrario, per precompilare la modifica: "Mario R." → { nome: 'Mario', cognome: 'R' }. */
const dividi = (s) => {
  const m = /^(.*\S)\s+(\p{L})\.$/u.exec(s || '');
  return m ? { nome: m[1], cognome: m[2] } : { nome: s || '', cognome: '' };
};

/** "2026-10-30" → "30/10/2026" */
const dataIt = (d) => (d ? d.split('-').reverse().join('/') : '');

/** Stato del contratto (calcolato dal server) come etichetta e colore. */
function ChipContratto({ a }) {
  if (a.tipo === 'interna') return null;
  const c = a.contratto || { stato: 'illimitato' };
  const [label, bg, fg] = {
    illimitato:  ['senza scadenza', '#f1f5f9', '#475569'],
    attivo:      [a.accessoAl ? `fino al ${dataIt(a.accessoAl)}` : 'attivo', '#ecfdf5', '#15803d'],
    in_scadenza: [c.giorni === 0 ? 'scade oggi' : `scade tra ${c.giorni} ${c.giorni === 1 ? 'giorno' : 'giorni'}`, '#fff7ed', '#c2410c'],
    scaduto:     [`scaduto il ${dataIt(a.accessoAl)}`, '#fef2f2', '#b91c1c'],
    futuro:      [`dal ${dataIt(a.accessoDal)}`, '#eff6ff', '#1d4ed8'],
  }[c.stato] || ['', '#f1f5f9', '#475569'];
  return <Chip size="small" label={label} sx={{ height: 18, fontSize: 10, ml: 0.7, bgcolor: bg, color: fg, fontWeight: 700 }} />;
}

const TIPI = {
  interna:        { etichetta: 'noi', icona: Business, colore: '#1d4ed8', sfondo: '#eff6ff' },
  concessionario: { etichetta: 'concessionaria', icona: Business, colore: '#c2410c', sfondo: '#fff7ed' },
  pianificazione: { etichetta: 'agenzia', icona: Campaign, colore: '#7c3aed', sfondo: '#f5f3ff' },
};

/** Nome e cognome affiancati, con l'anteprima di come comparirà nell'app. */
function CampiNome({ valore, onChange, errori }) {
  const avviso = errori.nome || errori.cognome;
  const anteprima = valore.nome.trim() && valore.cognome.trim() ? nomeBreve(valore.nome, valore.cognome) : null;
  return (
    <Box>
      <Stack direction="row" spacing={1}>
        <TextField size="small" label="Nome" value={valore.nome} fullWidth error={!!errori.nome}
                   autoComplete="off" onChange={(e) => onChange({ ...valore, nome: e.target.value })} />
        <TextField size="small" label="Cognome" value={valore.cognome} fullWidth error={!!errori.cognome}
                   autoComplete="off" onChange={(e) => onChange({ ...valore, cognome: e.target.value })} />
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mx: 1.75, color: avviso ? 'error.main' : 'text.secondary' }}>
        {avviso || (anteprima ? `Nell'app comparirà come «${anteprima}»: del cognome si salva solo l'iniziale`
                              : "Del cognome si salva solo l'iniziale")}
      </Typography>
    </Box>
  );
}

/** Scelta dell'azienda di un utente; per AffariAffissioni anche il ruolo. */
function CampiAzienda({ valore, onChange, aziende, bloccato }) {
  const az = aziende.find(a => a.id === Number(valore.aziendaId));
  const interna = !az || az.tipo === 'interna';
  return (
    <>
      <TextField size="small" select label="Azienda" value={valore.aziendaId} fullWidth disabled={bloccato}
                 helperText={bloccato ? 'Non puoi spostare te stesso in un’altra azienda'
                            : interna ? ' '
                            : az.tipo === 'pianificazione' ? `Consulterà in sola lettura gli impianti del contratto di ${az.nome}, senza voto`
                            : `Account di ${az.nome}: nome e cognome non servono, nell'app comparirà come «${az.nome}». Vedrà e aggiornerà solo i suoi impianti, senza voto`}
                 onChange={(e) => onChange({ ...valore, aziendaId: e.target.value, ruolo: 'operatore' })}>
        {aziende.filter(a => a.attiva || a.id === Number(valore.aziendaId)).map(a => (
          <MenuItem key={a.id} value={a.id}>{a.nome}{a.tipo === 'interna' ? ' (noi)' : ''}</MenuItem>
        ))}
      </TextField>
      {interna && (
        <TextField size="small" select label="Ruolo" value={valore.ruolo} fullWidth disabled={bloccato}
                   helperText={bloccato ? 'Non puoi cambiare il tuo stesso ruolo' : ' '}
                   onChange={(e) => onChange({ ...valore, ruolo: e.target.value })}>
          <MenuItem value="operatore">Operatore — censisce e modifica</MenuItem>
          <MenuItem value="amministratore">Amministratore — può anche eliminare e gestire aziende e utenti</MenuItem>
        </TextField>
      )}
    </>
  );
}

/**
 * Aziende e utenti, riservato agli amministratori. Elenco a schede invece che a
 * tabella: su un telefono una tabella a 5 colonne costringe a scorrere in
 * orizzontale, le schede no.
 */
export default function UsersDialog({ open, onClose, me, notify }) {
  const fullScreen = useMediaQuery('(max-width:700px)');
  const [scheda, setScheda] = useState('utenti');
  const [utenti, setUtenti] = useState([]);
  const [elenco, setElenco] = useState({ aziende: [], nonAssegnati: 0 });
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState(null);
  const [campi, setCampi] = useState({});          // errori sotto i singoli campi
  // moduli aperti (uno alla volta)
  const [nuovo, setNuovo] = useState(null);        // nuovo utente
  const [modifica, setModifica] = useState(null);  // { id, nome, cognome, ruolo, aziendaId }
  const [resetPw, setResetPw] = useState(null);    // { id, nome, password }
  const [nuovaAz, setNuovaAz] = useState(null);    // { nome, tipo } della nuova azienda
  const [contratto, setContratto] = useState(null); // { id, accessoDal, accessoAl, vedeTutti, vedeConcessionari, concessionari }
  const [rinomina, setRinomina] = useState(null);  // { id, nome }
  const [perTitolo, setPerTitolo] = useState(null); // { id, prefisso, conteggio }

  const aziende = elenco.aziende;
  const interna = aziende.find(a => a.tipo === 'interna');
  // l'account di una concessionaria è dell'azienda: niente nome e cognome (lo decide anche il server)
  const diConcessionaria = (v) => aziende.find(a => a.id === Number(v?.aziendaId))?.tipo === 'concessionario';

  const chiudiModuli = () => {
    setNuovo(null); setModifica(null); setResetPw(null); setNuovaAz(null); setRinomina(null); setPerTitolo(null);
    setContratto(null); setCampi({});
  };

  const carica = useCallback(async () => {
    setBusy(true); setErrore(null);
    try {
      const [u, a] = await Promise.all([auth.utenti(), auth.aziende()]);
      setUtenti(u); setElenco(a);
    } catch (e) { setErrore(e.message); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { if (open) { carica(); chiudiModuli(); } }, [open, carica]);

  /** Esegue un'azione, poi ricarica tutto. Un 409 (nome già usato) va sotto il suo campo. */
  const esegui = async (fn, messaggio, campo409) => {
    setBusy(true); setErrore(null);
    try {
      await fn();
      notify(messaggio, 'success');
      chiudiModuli();
      const [u, a] = await Promise.all([auth.utenti(), auth.aziende()]);
      setUtenti(u); setElenco(a);
    } catch (e) {
      if (e.status === 409 && campo409) setCampi({ [campo409]: e.message }); else setErrore(e.message);
    }
    finally { setBusy(false); }
  };

  /* ------------------------------------------------------------ utenti -- */
  const creaUtente = () => {
    const e = {};
    if (!diConcessionaria(nuovo)) {
      if (!nuovo.nome.trim()) e.nome = 'Scrivi il nome.';
      else if (!nuovo.cognome.trim()) e.cognome = "Scrivi il cognome: si salva solo l'iniziale.";
    }
    if (!/^[a-z0-9._-]{3,50}$/.test(nuovo.username))
      e.username = 'Da 3 a 50 caratteri: lettere minuscole, numeri, punto o trattino.';
    if (nuovo.password.length < 8) e.password = 'Almeno 8 caratteri.';
    if (nuovo.email && !EMAIL_OK.test(nuovo.email)) e.email = 'Email non valida.';
    setCampi(e);
    if (Object.keys(e).length === 0) esegui(() => auth.creaUtente(nuovo), `Utente ${nuovo.username} creato`, 'username');
  };

  const salvaModifica = (io) => {
    const conNome = !diConcessionaria(modifica);
    if (conNome && !modifica.nome.trim()) return setCampi({ nome: 'Scrivi il nome.' });
    if (modifica.email && !EMAIL_OK.test(modifica.email)) return setCampi({ email: 'Email non valida.' });
    const dati = conNome ? { id: modifica.id, nome: modifica.nome, cognome: modifica.cognome, email: modifica.email || null }
                         : { id: modifica.id, email: modifica.email || null };
    if (!io) {
      dati.aziendaId = modifica.aziendaId;
      const az = aziende.find(a => a.id === Number(modifica.aziendaId));
      if (!az || az.tipo === 'interna') dati.ruolo = modifica.ruolo;
    }
    esegui(() => auth.aggiornaUtente(dati), 'Modifiche salvate');
  };

  /* ----------------------------------------------------------- aziende -- */
  const creaAzienda = () => {
    if (!nuovaAz.nome.trim()) return setCampi({ azienda: "Scrivi il nome dell'azienda." });
    esegui(() => auth.creaAzienda({ nome: nuovaAz.nome, tipo: nuovaAz.tipo }),
           `${nuovaAz.tipo === 'pianificazione' ? 'Agenzia' : 'Concessionaria'} ${nuovaAz.nome.trim()} creata`, 'azienda');
  };

  const salvaContratto = (a) => {
    const c = contratto;
    if (c.accessoDal && c.accessoAl && c.accessoDal > c.accessoAl) return setCampi({ contratto: "La fine è prima dell'inizio." });
    const dati = { id: a.id, accessoDal: c.accessoDal || null, accessoAl: c.accessoAl || null };
    if (a.tipo === 'pianificazione') Object.assign(dati, { vedeTutti: c.vedeTutti, vedeConcessionari: c.vedeConcessionari, concessionari: c.concessionari });
    esegui(() => auth.aggiornaAzienda(dati), `Contratto di ${a.nome} aggiornato`);
  };
  const concessionariTutti = aziende.filter(z => z.tipo === 'concessionario');

  const conta = async () => {
    setBusy(true); setErrore(null);
    try {
      const r = await auth.assegnaPerTitolo(perTitolo.id, perTitolo.prefisso, false);
      setPerTitolo(p => ({ ...p, conteggio: r }));
    } catch (e) { setErrore(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1, pb: 0 }}>
        <AdminPanelSettings color="primary" />
        <Box flex={1}>Aziende e utenti</Box>
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <Tabs value={scheda} onChange={(_, v) => { setScheda(v); chiudiModuli(); setErrore(null); }}
            variant="fullWidth" sx={{ borderBottom: '1px solid #e2e8f0' }}>
        <Tab value="utenti" label={`Utenti (${utenti.length})`} />
        <Tab value="aziende" label={`Aziende (${aziende.length})`} />
      </Tabs>

      <DialogContent sx={{ px: { xs: 1.5, sm: 3 } }}>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          {errore && <Alert severity="error" onClose={() => setErrore(null)}>{errore}</Alert>}
          {busy && <Stack alignItems="center" py={1}><CircularProgress size={26} /></Stack>}

          {scheda === 'utenti' && (<>
            {nuovo ? (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" gutterBottom>Nuovo utente</Typography>
                <Stack spacing={1.5}>
                  <CampiAzienda valore={nuovo} onChange={setNuovo} aziende={aziende} />
                  {!diConcessionaria(nuovo) && <CampiNome valore={nuovo} onChange={setNuovo} errori={campi} />}
                  <TextField size="small" label="Nome utente" value={nuovo.username} fullWidth
                             error={!!campi.username} autoComplete="off"
                             helperText={campi.username || 'Minuscolo, senza spazi — è quello che digiterà per entrare'}
                             inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
                             onChange={(e) => setNuovo({ ...nuovo, username: e.target.value.toLowerCase().replace(/\s+/g, '') })} />
                  <CampoEmail valore={nuovo} onChange={setNuovo} errore={campi.email} />
                  <TextField size="small" label="Password provvisoria" value={nuovo.password} fullWidth
                             error={!!campi.password} autoComplete="new-password"
                             helperText={campi.password || "Gliela comunichi tu: l'app gli chiederà di cambiarla al primo accesso"}
                             onChange={(e) => setNuovo({ ...nuovo, password: e.target.value })} />
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={creaUtente} disabled={busy} fullWidth>Crea utente</Button>
                    <Button onClick={chiudiModuli}>Annulla</Button>
                  </Stack>
                </Stack>
              </Paper>
            ) : (
              <Button startIcon={<PersonAdd />} variant="outlined"
                      onClick={() => { chiudiModuli(); setErrore(null); setNuovo({ ...VUOTO, aziendaId: interna?.id ?? '' }); }}>
                Nuovo utente
              </Button>
            )}

            <Divider />

            {utenti.map((u) => {
              const io = u.id === me.id;
              const esterno = u.azienda && u.azienda.tipo !== 'interna';
              return (
                <Paper key={u.id} variant="outlined"
                       sx={{ p: 1.5, opacity: u.attivo ? 1 : 0.6, borderColor: u.attivo ? 'divider' : '#fca5a5' }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    {u.amministratore ? <AdminPanelSettings sx={{ color: '#1d4ed8' }} />
                      : esterno ? <Business sx={{ color: '#c2410c' }} /> : <Person sx={{ color: '#64748b' }} />}
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {u.nome} {io && <Chip size="small" label="tu" sx={{ height: 18, fontSize: 10, ml: 0.5 }} />}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {u.username} · {esterno ? u.azienda.nome : (u.amministratore ? 'amministratore' : 'operatore')}
                        {!u.attivo && ' · disattivato'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {u.email || 'senza email'} · {u.ultimoAccesso ? `ultimo accesso ${u.ultimoAccesso}` : 'mai entrato'}
                        {u.deveCambiare && ' · deve cambiare password'}
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      <Tooltip title="Modifica nome, email, azienda e ruolo">
                        <IconButton size="small" onClick={() => {
                          chiudiModuli(); setErrore(null);
                          setModifica({ id: u.id, ...dividi(u.nome), ruolo: u.ruolo, aziendaId: u.azienda?.id ?? '', email: u.email || '' });
                        }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reimposta la password">
                        <IconButton size="small" onClick={() => { chiudiModuli(); setErrore(null); setResetPw({ id: u.id, nome: u.nome, password: '' }); }}>
                          <LockReset fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={io ? 'Non puoi disattivare te stesso' : (u.attivo ? 'Disattiva' : 'Riattiva')}>
                        <span>
                          <IconButton size="small" disabled={io}
                                      onClick={() => esegui(() => auth.aggiornaUtente({ id: u.id, attivo: !u.attivo }),
                                                            u.attivo ? `${u.nome} disattivato` : `${u.nome} riattivato`)}>
                            {u.attivo ? <Block fontSize="small" color="warning" /> : <CheckCircle fontSize="small" color="success" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  {modifica?.id === u.id && (
                    <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                      <CampiAzienda valore={modifica} onChange={setModifica} aziende={aziende} bloccato={io} />
                      {!diConcessionaria(modifica) && <CampiNome valore={modifica} onChange={setModifica} errori={campi} />}
                      <CampoEmail valore={modifica} onChange={setModifica} errore={campi.email} />
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" fullWidth disabled={busy}
                                onClick={() => salvaModifica(io)}>Salva</Button>
                        <Button size="small" onClick={chiudiModuli}>Annulla</Button>
                      </Stack>
                    </Stack>
                  )}

                  {resetPw?.id === u.id && (
                    <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                      <TextField size="small" label="Nuova password provvisoria" value={resetPw.password} fullWidth
                                 autoComplete="new-password"
                                 helperText="Verrà chiesto di cambiarla al primo accesso. Le sessioni aperte vengono chiuse."
                                 onChange={(e) => setResetPw({ ...resetPw, password: e.target.value })} />
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" color="warning" fullWidth disabled={busy}
                                onClick={() => esegui(() => auth.aggiornaUtente({ id: u.id, password: resetPw.password }),
                                                      `Password di ${u.nome} reimpostata`)}>Reimposta</Button>
                        <Button size="small" onClick={chiudiModuli}>Annulla</Button>
                      </Stack>
                    </Stack>
                  )}
                </Paper>
              );
            })}
          </>)}

          {scheda === 'aziende' && (<>
            <Typography variant="body2" color="text.secondary">
              Impianti non ancora assegnati a una concessionaria: <b>{elenco.nonAssegnati}</b>.
              Una concessionaria vede e aggiorna solo i propri impianti; un'agenzia consulta, in sola
              lettura, quelli del suo contratto. Nessuna delle due vede il voto.
            </Typography>

            {nuovaAz !== null ? (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" gutterBottom>Nuova azienda</Typography>
                <Stack spacing={1.5}>
                  <TextField size="small" select label="Tipo" value={nuovaAz.tipo} fullWidth
                             onChange={(e) => setNuovaAz({ ...nuovaAz, tipo: e.target.value })}>
                    <MenuItem value="concessionario">Concessionaria — proprietaria di impianti</MenuItem>
                    <MenuItem value="pianificazione">Agenzia o pianificazione — consulta il catalogo</MenuItem>
                  </TextField>
                  <TextField size="small" label="Nome dell'azienda" value={nuovaAz.nome} fullWidth autoFocus autoComplete="off"
                             error={!!campi.azienda}
                             helperText={campi.azienda || (nuovaAz.tipo === 'concessionario'
                               ? "Scrivila come nei titoli (es. ALESSI): l'app la riconosce nei titoli e nei filtri. Gli utenti si possono aggiungere dopo, o mai"
                               : 'Dopo averla creata imposti il contratto: date e impianti che può consultare')}
                             onChange={(e) => setNuovaAz({ ...nuovaAz, nome: e.target.value })} />
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={creaAzienda} disabled={busy} fullWidth>Crea azienda</Button>
                    <Button onClick={chiudiModuli}>Annulla</Button>
                  </Stack>
                </Stack>
              </Paper>
            ) : (
              <Button startIcon={<AddBusiness />} variant="outlined"
                      onClick={() => { chiudiModuli(); setErrore(null); setNuovaAz({ nome: '', tipo: 'concessionario' }); }}>
                Nuova azienda
              </Button>
            )}

            <Divider />

            {aziende.map((a) => {
              const noi = a.tipo === 'interna';
              const agenzia = a.tipo === 'pianificazione';
              const t = TIPI[a.tipo] || TIPI.concessionario;
              const Icona = t.icona;
              const c = perTitolo?.id === a.id ? perTitolo.conteggio : null;
              const nomiScelti = a.concessionari.map(id => aziende.find(z => z.id === id)?.nome).filter(Boolean);
              const cosaVede = noi ? 'vede tutti gli impianti'
                : !agenzia ? `${a.impianti} ${a.impianti === 1 ? 'impianto' : 'impianti'}`
                : a.vedeTutti ? 'consulta tutti gli impianti'
                : nomiScelti.length ? `consulta gli impianti di ${nomiScelti.join(', ')}` : 'nessuna concessionaria scelta';
              return (
                <Paper key={a.id} variant="outlined"
                       sx={{ p: 1.5, opacity: a.attiva ? 1 : 0.6, borderColor: a.attiva ? 'divider' : '#fca5a5' }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Icona sx={{ color: t.colore }} />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {a.nome}
                        <Chip size="small" label={t.etichetta}
                              sx={{ height: 18, fontSize: 10, ml: 0.7, bgcolor: t.sfondo, color: t.colore }} />
                        <ChipContratto a={a} />
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {a.utenti} {a.utenti === 1 ? 'utente' : 'utenti'} · {cosaVede}
                        {agenzia && a.vedeConcessionari && ' · vede i nomi delle concessionarie'}
                        {!a.attiva && ' · sospesa'}
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      <Tooltip title="Rinomina">
                        <IconButton size="small" onClick={() => { chiudiModuli(); setErrore(null); setRinomina({ id: a.id, nome: a.nome }); }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* contratto a tempo solo per le agenzie: le concessionarie hanno accesso illimitato */}
                      {agenzia && (
                        <Tooltip title="Contratto e visibilità">
                          <IconButton size="small" onClick={() => {
                            chiudiModuli(); setErrore(null);
                            setContratto({ id: a.id, accessoDal: a.accessoDal || '', accessoAl: a.accessoAl || '',
                                           vedeTutti: a.vedeTutti, vedeConcessionari: a.vedeConcessionari, concessionari: [...a.concessionari] });
                          }}>
                            <EventNote fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!noi && (<>
                        {!agenzia && <Tooltip title="Assegna impianti in base al titolo">
                          <IconButton size="small" onClick={() => {
                            chiudiModuli(); setErrore(null);
                            setPerTitolo({ id: a.id, prefisso: a.nome.toUpperCase(), conteggio: null });
                          }}>
                            <PlaylistAdd fontSize="small" />
                          </IconButton>
                        </Tooltip>}
                        <Tooltip title={a.attiva ? 'Sospendi: i suoi utenti escono subito' : 'Riattiva'}>
                          <IconButton size="small"
                                      onClick={() => esegui(() => auth.aggiornaAzienda({ id: a.id, attiva: !a.attiva }),
                                                            a.attiva ? `${a.nome} sospesa` : `${a.nome} riattivata`)}>
                            {a.attiva ? <Block fontSize="small" color="warning" /> : <CheckCircle fontSize="small" color="success" />}
                          </IconButton>
                        </Tooltip>
                      </>)}
                    </Stack>
                  </Stack>

                  {rinomina?.id === a.id && (
                    <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                      <TextField size="small" label="Nome dell'azienda" value={rinomina.nome} fullWidth autoComplete="off"
                                 error={!!campi.azienda} helperText={campi.azienda || ' '}
                                 onChange={(e) => setRinomina({ ...rinomina, nome: e.target.value })} />
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" fullWidth disabled={busy || !rinomina.nome.trim()}
                                onClick={() => esegui(() => auth.aggiornaAzienda({ id: a.id, nome: rinomina.nome }), 'Nome aggiornato', 'azienda')}>
                          Salva
                        </Button>
                        <Button size="small" onClick={chiudiModuli}>Annulla</Button>
                      </Stack>
                    </Stack>
                  )}

                  {contratto?.id === a.id && (
                    <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                      <Stack direction="row" spacing={1}>
                        <TextField size="small" type="date" label="Accesso dal" value={contratto.accessoDal} fullWidth
                                   InputLabelProps={{ shrink: true }} error={!!campi.contratto}
                                   onChange={(e) => setContratto({ ...contratto, accessoDal: e.target.value })} />
                        <TextField size="small" type="date" label="Accesso fino al" value={contratto.accessoAl} fullWidth
                                   InputLabelProps={{ shrink: true }} error={!!campi.contratto}
                                   onChange={(e) => setContratto({ ...contratto, accessoAl: e.target.value })} />
                      </Stack>
                      <Typography variant="caption" sx={{ mx: 1.75, color: campi.contratto ? 'error.main' : 'text.secondary' }}>
                        {campi.contratto || 'Date comprese; vuote = senza limite. Alla scadenza i suoi utenti non entrano più, e il messaggio spiega perché.'}
                      </Typography>
                      {agenzia && (<>
                        <FormControlLabel
                          control={<Switch checked={contratto.vedeTutti}
                                           onChange={(e) => setContratto({ ...contratto, vedeTutti: e.target.checked })} />}
                          label={<Typography variant="body2">Consulta tutti gli impianti</Typography>} />
                        {!contratto.vedeTutti && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Solo gli impianti di:</Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                              {concessionariTutti.map(z => {
                                const on = contratto.concessionari.includes(z.id);
                                return <Chip key={z.id} size="small" label={z.nome} color={on ? 'primary' : 'default'}
                                             variant={on ? 'filled' : 'outlined'}
                                             onClick={() => setContratto({ ...contratto, concessionari: on
                                               ? contratto.concessionari.filter(x => x !== z.id) : [...contratto.concessionari, z.id] })} />;
                              })}
                              {!concessionariTutti.length && (
                                <Typography variant="caption" color="text.secondary">
                                  Nessuna concessionaria registrata: aggiungila con «Nuova azienda».
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        )}
                        <FormControlLabel
                          control={<Switch checked={contratto.vedeConcessionari}
                                           onChange={(e) => setContratto({ ...contratto, vedeConcessionari: e.target.checked })} />}
                          label={<Typography variant="body2">Vede il nome della concessionaria (nel titolo e nella scheda)</Typography>} />
                      </>)}
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" fullWidth disabled={busy} onClick={() => salvaContratto(a)}>Salva</Button>
                        <Button size="small" onClick={chiudiModuli}>Annulla</Button>
                      </Stack>
                    </Stack>
                  )}

                  {perTitolo?.id === a.id && (
                    <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                      <TextField size="small" label="Il titolo inizia con" value={perTitolo.prefisso} fullWidth autoComplete="off"
                                 helperText="Si assegnano gli impianti non ancora assegnati il cui titolo comincia così"
                                 onChange={(e) => setPerTitolo({ ...perTitolo, prefisso: e.target.value, conteggio: null })} />
                      {c && (
                        <Alert severity={c.trovati ? 'info' : 'warning'}>
                          {c.trovati ? `${c.trovati} impianti da assegnare a ${a.nome}.` : 'Nessun impianto da assegnare con questo inizio.'}
                          {c.giaSuoi > 0 && ` ${c.giaSuoi} sono già suoi.`}
                          {c.giaDiAltri > 0 && ` ${c.giaDiAltri} sono già di altre concessionarie e restano a loro.`}
                        </Alert>
                      )}
                      <Stack direction="row" spacing={1}>
                        {!c ? (
                          <Button size="small" variant="contained" fullWidth disabled={busy || perTitolo.prefisso.trim().length < 2}
                                  onClick={conta}>Conta gli impianti</Button>
                        ) : (
                          <Button size="small" variant="contained" fullWidth disabled={busy || !c.trovati}
                                  onClick={() => esegui(() => auth.assegnaPerTitolo(a.id, perTitolo.prefisso, true),
                                                        `${c.trovati} impianti assegnati a ${a.nome}`)}>
                            Assegna {c.trovati} impianti
                          </Button>
                        )}
                        <Button size="small" onClick={chiudiModuli}>Annulla</Button>
                      </Stack>
                    </Stack>
                  )}
                </Paper>
              );
            })}
          </>)}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
}
